import { useEffect, useMemo } from 'react';

import { t } from 'i18next';
import classnames from 'classnames';
import { DateTime } from 'luxon';

import {
  TeacherGetAttendancesQuery,
  useGetAttendancesQuery,
} from '@school_ify/schoolify-repository/src/teacher/types';

import { useAppDispatch, useAppSelector } from 'redux/hooks';
import useRouterHelper from 'hooks/useRouterHelper';
import { getSubjectsThunk, studySubjectsState } from 'redux/study/studySubjects.slice';
import PeriodsTable from 'views/Attendance/PeriodsTable/PeriodsTable';
import { getUser } from 'redux/authentication/auth.slice';
import UpcomingProvider from 'application/dashboard/upcomings/upcoming.context';

import FilterBar from './components/FilterBar/FilterBar';

import './Attendance.scoped.scss';
import useSemesters from 'clean/apiHooks/useSemesters';

type AttendanceI = TeacherGetAttendancesQuery['attendances'][0];

export interface ItemI extends AttendanceI {
  dateYMD: string;
}

export interface IAttendanceQueryInterface {
  Class?: string;
  selected_subject?: string;
  Subjects?: string;
  selectedClassId?: string;
}

const Attendance: React.FC = () => {
  const { query, routePush } = useRouterHelper();
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const subjectsList = useAppSelector(studySubjectsState);
  const { semesterId, semester } = useSemesters();

  const classIdList = query.Class ? (query.Class as string).split(',') : [];
  const subjectIdList = query.Subjects ? (query.Subjects as string).split(',') : [];

  const {
    data: attendanceData,
    loading: loadingAttendance,
    refetch: refetchAttendances,
  } = useGetAttendancesQuery({
    skip: !semesterId,
    variables: {
      where: {
        semester_id: {
          _eq: semesterId,
        },
        class_id: {
          _eq: query?.selectedClassId as string,
        },
        subject_id: {
          _in: subjectIdList.map(
            id => subjectsList.subjects.find(s => s.subject_id === id)?.teaching_structure_id
          ),
        },
      },
    },
  });
  const attendances: ItemI[] = useMemo(() => {
    return (
      attendanceData?.attendances?.map(item => ({
        ...item,
        dateYMD: DateTime.fromISO(item.date).toFormat('y-MM-dd'),
      })) || []
    );
  }, [attendanceData]);

  const startDate = DateTime.fromISO(semester?.startDate);
  const endDate = DateTime.fromISO(semester?.endDate);

  const realEndDate =
    DateTime.now().plus({ day: 14 }) > endDate ? endDate : DateTime.now().plus({ day: 14 });
  const currentDate = DateTime.now().toFormat('y-MM-dd');
  const dates = useMemo(() => {
    const result = [];
    if (semester) {
      let cursor = startDate.startOf('day');
      while (cursor < realEndDate) {
        result.push(cursor.toFormat('y-MM-dd'));
        cursor = cursor.plus({ days: 1 });
      }
    }
    return result;
  }, [semester]);

  const handleClassChange = str => () => {
    routePush({
      search: {
        selectedClassId: str,
      },
    });
  };

  const getClassTitle = id => {
    const entity = subjectsList.subjects.find(entry => entry.teaching_structure.parent.id === id);

    if (entity) {
      return entity.teaching_structure.parent.title;
    }

    return null;
  };

  useEffect(() => {
    dispatch(getSubjectsThunk(user.id));
  }, []);

  if (subjectsList.subjects_loading !== 'success') {
    return null;
  }

  return (
    <UpcomingProvider>
      <FilterBar />
      <div className="grades-container">
        <div className="classes-menu">
          <div className="title">{t('grades:class')}:</div>

          {classIdList.map(id => {
            return (
              <div
                key={id}
                className={classnames({
                  item: true,
                  active: query.selectedClassId === id,
                })}
                onClick={handleClassChange(id)}
              >
                <span>{getClassTitle(id)}</span>
              </div>
            );
          })}
        </div>

        <div key={'content_table_container'} className="card-body-wrapper">
          {attendances &&
            semesterId &&
            !loadingAttendance &&
            query.Class &&
            subjectIdList.map(id => {
              const subject = subjectsList.subjects.find(
                ({ subject_id: subjectId }) => subjectId === id
              );
              return (
                <PeriodsTable
                  currentDate={currentDate}
                  semesterId={semesterId}
                  refetchAttendances={refetchAttendances}
                  dates={dates}
                  attendances={attendances}
                  key={`${subject.subject_id}_${query.selectedClassId as string}`}
                  subject={subject}
                  classId={query.selectedClassId as string}
                  classTitle={getClassTitle(query.selectedClassId as string)}
                />
              );
            })}
        </div>
      </div>
    </UpcomingProvider>
  );
};

export default Attendance;
